import { useEffect, useState } from "react";
import { Dropdown, Form } from "semantic-ui-react";

/* 12-hour AM/PM time picker that stores/emits the same 24h "HH:MM" string a
   native <input type="time"> would — a drop-in replacement wherever that was
   used, but with a display format that doesn't depend on OS/browser locale
   (a native time input renders 24h on many locales even when the user wants
   AM/PM). Minute options are 5-minute increments; a pre-existing value whose
   minutes don't land on a 5-minute mark (e.g. old data) simply won't match
   any option until the user picks one — an accepted narrowing vs. the native
   input's free-form minutes. */

const HOURS = Array.from({ length: 12 }, (_, i) => {
  const v = String(i + 1).padStart(2, "0");
  return { key: v, value: v, text: v };
});
const MINUTES = Array.from({ length: 12 }, (_, i) => {
  const v = String(i * 5).padStart(2, "0");
  return { key: v, value: v, text: v };
});
const PERIODS = [
  { key: "AM", value: "AM", text: "AM" },
  { key: "PM", value: "PM", text: "PM" },
];

function to24Hour(h12, period) {
  let h = Number(h12) % 12;
  if (period === "PM") h += 12;
  return String(h).padStart(2, "0");
}

function from24(value) {
  if (!value) return { h12: "", m: "", period: "" };
  const [hStr, mStr] = value.split(":");
  const h24 = Number(hStr);
  const period = h24 >= 12 ? "PM" : "AM";
  const h12raw = h24 % 12;
  const h12 = String(h12raw === 0 ? 12 : h12raw).padStart(2, "0");
  return { h12, m: mStr, period };
}

export default function TimeInput12h({
  label,
  value,
  onChange,
  required = false,
  error = false,
  disabled = false,
  ariaLabel,
}) {
  /* Tracked locally rather than derived fresh from `value` on every render:
     a partial selection (e.g. just the hour, starting from a blank field)
     has nowhere else to live until all three parts are chosen and `onChange`
     fires. Deriving straight from `value` meant each dropdown's own
     onChange only ever saw the OTHER two parts as still blank -- since
     `value` never updates until emit succeeds -- so completing a blank
     field was impossible: picking hour, then minute, then period each
     bailed in turn, forever. */
  const [parts, setParts] = useState(() => from24(value));
  useEffect(() => {
    setParts(from24(value));
  }, [value]);
  const { h12, m, period } = parts;

  /* Only emit once all three parts are chosen, so we never silently write a
     bogus time from a partial selection. */
  const emit = (nextH12, nextM, nextPeriod) => {
    setParts({ h12: nextH12, m: nextM, period: nextPeriod });
    if (!nextH12 || !nextM || !nextPeriod) return;
    onChange(`${to24Hour(nextH12, nextPeriod)}:${nextM}`);
  };

  return (
    <Form.Field required={required} error={Boolean(error)} disabled={disabled}>
      {label ? (typeof label === "string" ? <label>{label}</label> : label) : null}
      <div style={{ display: "flex", gap: 6 }} aria-label={ariaLabel}>
        <Dropdown
          selection
          compact
          placeholder="HH"
          disabled={disabled}
          options={HOURS}
          value={h12 || undefined}
          onChange={(_, d) => emit(d.value, m, period)}
          style={{ minWidth: 68 }}
        />
        <Dropdown
          selection
          compact
          placeholder="MM"
          disabled={disabled}
          options={MINUTES}
          value={m || undefined}
          onChange={(_, d) => emit(h12, d.value, period)}
          style={{ minWidth: 68 }}
        />
        <Dropdown
          selection
          compact
          placeholder="AM/PM"
          disabled={disabled}
          options={PERIODS}
          value={period || undefined}
          onChange={(_, d) => emit(h12, m, d.value)}
          style={{ minWidth: 90 }}
        />
      </div>
    </Form.Field>
  );
}
